import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Crop, Plus, RotateCcw, Sparkles, Trash2, X } from 'lucide-react';
import { api } from '../api';
import { analyzeImageForCrops, clampCropRect, type CropMethod, type CropRect } from '../imageCrop';

type EditableCrop = CropRect & { id: string };
type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

const createCrop = (crop: CropRect): EditableCrop => ({ ...crop, id: crypto.randomUUID() });

function editedRect(rect: CropRect, mode: DragMode, dx: number, dy: number) {
  if (mode === 'move') return clampCropRect({ ...rect, x: rect.x + dx, y: rect.y + dy, method: 'manual' });
  let left = rect.x; let top = rect.y; let right = rect.x + rect.width; let bottom = rect.y + rect.height;
  if (mode.includes('w')) left = Math.min(right - .06, Math.max(0, left + dx));
  if (mode.includes('e')) right = Math.max(left + .06, Math.min(1, right + dx));
  if (mode.includes('n')) top = Math.min(bottom - .06, Math.max(0, top + dy));
  if (mode.includes('s')) bottom = Math.max(top + .06, Math.min(1, bottom + dy));
  return clampCropRect({ x: left, y: top, width: right - left, height: bottom - top, method: 'manual' });
}

export function PhotoCropper({ offerId, photo, onClose, onSaved }: {
  offerId: string;
  photo: { id: string; url: string };
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; mode: DragMode; id: string; x: number; y: number; rect: CropRect } | null>(null);
  const [dimensions, setDimensions] = useState({ width: 9, height: 16 });
  const [crops, setCrops] = useState<EditableCrop[]>([]);
  const [activeId, setActiveId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hideOriginal, setHideOriginal] = useState(true);
  const [message, setMessage] = useState('Szukam zdjęć na screenshocie…');
  const [error, setError] = useState('');
  const active = useMemo(() => crops.find(crop => crop.id === activeId) || crops[0], [activeId, crops]);

  const analyze = async () => {
    setLoading(true); setError(''); setMessage('Szukam zdjęć na screenshocie…');
    try {
      const result = await analyzeImageForCrops(photo.url);
      setDimensions({ width: result.width, height: result.height });
      const suggestions = result.suggestions.length ? result.suggestions : [{ x: .06, y: .08, width: .88, height: .72, method: 'manual' as const }];
      const editable = suggestions.map(createCrop);
      setCrops(editable); setActiveId(editable[0].id);
      setMessage(result.suggestions.length
        ? `Znaleziono ${result.suggestions.length} ${result.suggestions.length === 1 ? 'propozycję zdjęcia' : result.suggestions.length < 5 ? 'propozycje zdjęć' : 'propozycji zdjęć'}. Sprawdź ramki przed zapisem.`
        : 'Nie udało się pewnie rozdzielić zdjęć. Ustaw ramkę ręcznie.');
    } catch (cause) {
      setCrops([createCrop({ x: .06, y: .08, width: .88, height: .72, method: 'manual' })]);
      setMessage('Automatyczne wykrywanie nie jest dostępne dla tego pliku. Ustaw ramkę ręcznie.');
      setError(cause instanceof Error ? cause.message : 'Nie udało się przeanalizować zdjęcia.');
    } finally { setLoading(false); }
  };

  useEffect(() => { void analyze(); }, [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const previous = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  const startDrag = (event: PointerEvent, crop: EditableCrop, mode: DragMode) => {
    event.preventDefault(); event.stopPropagation(); setActiveId(crop.id);
    stage.current?.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, mode, id: crop.id, x: event.clientX, y: event.clientY, rect: crop };
  };
  const moveDrag = (event: PointerEvent) => {
    const current = drag.current; const bounds = stage.current?.getBoundingClientRect();
    if (!current || current.pointerId !== event.pointerId || !bounds) return;
    const next = editedRect(current.rect, current.mode, (event.clientX - current.x) / bounds.width, (event.clientY - current.y) / bounds.height);
    setCrops(items => items.map(item => item.id === current.id ? { ...next, id: item.id } : item));
  };
  const endDrag = (event: PointerEvent) => {
    if (drag.current?.pointerId === event.pointerId) { stage.current?.releasePointerCapture(event.pointerId); drag.current = null; }
  };
  const addCrop = () => {
    const crop = createCrop({ x: .1, y: .12, width: .8, height: .55, method: 'manual' });
    setCrops(items => [...items, crop]); setActiveId(crop.id);
  };
  const removeActive = () => {
    if (!active) return;
    setCrops(items => { const next = items.filter(item => item.id !== active.id); setActiveId(next[0]?.id || ''); return next; });
  };
  const save = async () => {
    if (!crops.length) return;
    setSaving(true); setError('');
    try {
      await api(`/api/offers/${offerId}/crops`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sourceMediaId: photo.id, crops: crops.map(({ id: _id, ...crop }) => crop), hideOriginal }),
      });
      await onSaved();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Nie udało się zapisać wycinków.'); }
    finally { setSaving(false); }
  };

  return <div className="crop-modal" role="dialog" aria-modal="true" aria-label="Wydziel zdjęcia ze screenshota">
    <header><div><Crop /><p><strong>Wydziel zdjęcia</strong><span>Oryginał pozostanie w materiałach źródłowych.</span></p></div><button onClick={onClose} aria-label="Zamknij kadrowanie"><X /></button></header>
    <div className="crop-modal-body">
      <section className="crop-workspace">
        <div className="crop-stage" ref={stage} style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
          <img src={photo.url} alt="Materiał do kadrowania" draggable={false} />
          {crops.map((crop, index) => <div
            key={crop.id}
            className={crop.id === active?.id ? 'crop-selection active' : 'crop-selection'}
            style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }}
            onPointerDown={event => startDrag(event, crop, 'move')}
            aria-label={`Wycinek ${index + 1}`}
          >
            <span>{index + 1}</span>
            {crop.id === active?.id && ['nw','ne','sw','se'].map(mode => <i key={mode} className={`crop-handle ${mode}`} onPointerDown={event => startDrag(event, crop, mode as DragMode)} />)}
          </div>)}
          {loading && <div className="crop-loading"><Sparkles />Analizuję układ…</div>}
        </div>
      </section>
      <aside className="crop-controls">
        <div className="crop-message"><Sparkles /><p><strong>Automatyczne propozycje bez AI</strong><span>{message}</span></p></div>
        <div className="crop-chips">{crops.map((crop, index) => <button className={crop.id === active?.id ? 'active' : ''} key={crop.id} onClick={() => setActiveId(crop.id)}><span>{index + 1}</span>Wycinek {index + 1}{crop.method === 'automatic' && <small>auto</small>}</button>)}</div>
        <div className="crop-actions"><button onClick={addCrop}><Plus />Dodaj wycinek</button><button onClick={removeActive} disabled={!active}><Trash2 />Usuń ramkę</button><button onClick={analyze} disabled={loading}><RotateCcw />Wykryj ponownie</button></div>
        <label className="crop-original-option"><input type="checkbox" checked={hideOriginal} onChange={event => setHideOriginal(event.target.checked)} /><span><strong>Usuń oryginał z galerii</strong><small>Zdjęcie będzie można przywrócić przez 30 dni. Materiał źródłowy pozostanie bezpiecznie zapisany.</small></span></label>
        {error && <p className="crop-error">{error}</p>}
        <p className="crop-help">Przeciągnij środek ramki, aby ją przesunąć. Złap narożnik, aby zmienić rozmiar. Materiał źródłowy nie jest bezpowrotnie kasowany.</p>
      </aside>
    </div>
    <footer><button className="secondary-button" onClick={onClose}>Anuluj</button><button className="primary-button" disabled={!crops.length || saving || loading} onClick={save}><Check />{saving ? 'Wycinam…' : `Zapisz ${crops.length || ''} ${crops.length === 1 ? 'zdjęcie' : 'zdjęcia'}`}</button></footer>
  </div>;
}
